// Hooks shared by the daily-cycle action panels.
import { useState } from 'react'
import { VERSIONLESS_COMMANDS } from '@/domain/types'
import type { CommandArgs, CommandName, PermitBundle } from '@/domain/types'
import { PtwError } from '@/lib/commands'
import { useCommand, usePermit } from '@/lib/queries'
import { serverNowMs } from './logic'

/**
 * "Now" for form defaults, corrected by the offset between the server clock and this
 * device seen when the permit was read. Defaults only: the server stamps every
 * signature and checks every time against its own clock.
 */
export function usePanelClock(bundle: PermitBundle): () => number {
  const q = usePermit(bundle.permit.id)
  const receivedAt = q.dataUpdatedAt
  return () => serverNowMs(bundle.generated_at, receivedAt, Date.now())
}

/**
 * Runs one command for a panel. Versioned commands send the row version the signer
 * was shown when the panel opened. If the permit changes while the panel is open
 * (the page refreshes every 30 s, and a stale-version refusal triggers a refresh),
 * the signer must acknowledge the change before signing, so nobody signs over a state
 * they have not seen. Versionless commands (gas test, attendance) send no version.
 *
 * The idempotency key is kept across edits after an ambiguous network failure: if the
 * first attempt did commit, the server answers IDEMPOTENCY_CONFLICT instead of
 * recording a second signature.
 */
export function usePanelCommand<C extends CommandName>(name: C, bundle: PermitBundle, onDone: () => void) {
  const cmd = useCommand(name)
  const q = usePermit(bundle.permit.id)
  const versionless = VERSIONLESS_COMMANDS.includes(name)
  const [seenVersion, setSeenVersion] = useState(bundle.permit.row_version)
  const changed = !versionless && bundle.permit.row_version !== seenVersion
  const changes = changed ? bundle.events.filter((e) => (e.new_row_version ?? 0) > seenVersion) : []

  async function submit(args: CommandArgs[C]): Promise<void> {
    try {
      await cmd.run(args, versionless ? undefined : seenVersion)
      onDone()
    } catch (e) {
      // The error is shown from cmd.error. A stale version means the permit moved on:
      // reload it so the change can be reviewed and acknowledged.
      if (PtwError.from(e).code === 'PW002') void q.refetch()
    }
  }

  function acknowledgeChanges(): void {
    setSeenVersion(bundle.permit.row_version)
    cmd.reset()
  }

  return {
    submit,
    pending: cmd.isPending,
    error: cmd.error,
    changed,
    changes,
    acknowledgeChanges,
  }
}

/** What the panel frame needs to show progress, errors and version changes. */
export type PanelCommandState = Pick<ReturnType<typeof usePanelCommand>, 'pending' | 'error' | 'changed' | 'changes' | 'acknowledgeChanges'>
